// SCR-STAFF-402 (AP-BIL-04): واریز نقد صندوق به بانک با فهرست واریزها (US-BIL-044) و صف پرداخت‌های کارتخوان با نتیجه نامعلوم برای تعیین تکلیف با
// دلیل (US-BIL-032؛ AC-BIL-032.2) — هر دو با bil.cash_session.approve.
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BilBankDepositView, type BilPendingPaymentView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, JalaliDate, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BankDepositDialog } from './FinanceDialogs';
import { Amount, bilKeys } from './common';

/** واریز نقد به بانک (AC-BIL-044.1). */
export function BankDepositsPanel() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const registers = useQuery({ queryKey: bilKeys.registers(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/cash-registers')) });
  const [registerId, setRegisterId] = useState('');
  const [open, setOpen] = useState(false);
  const active = (registers.data?.items ?? []).filter((register) => register.isActive);
  const selected = active.find((register) => register.id === registerId) ?? active[0];
  const deposits = useQuery({
    queryKey: bilKeys.bankDeposits(session?.tenantId, selected?.id ?? ''),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/bank-deposits', { params: { query: { registerId: selected?.id } } })),
    enabled: selected !== undefined,
  });
  const columns: DataTableColumn<BilBankDepositView>[] = [
    { id: 'date', headerKey: 'bil:bank_deposit.date', cell: (row) => <JalaliDate value={row.depositedOn} /> },
    { id: 'account', headerKey: 'bil:bank_deposit.account', cell: (row) => row.bankAccountTitle },
    { id: 'slip', headerKey: 'bil:bank_deposit.slip', cell: (row) => <bdi dir="ltr">{row.slipNumber}</bdi> },
    { id: 'amount', headerKey: 'bil:cashier.amount', cell: (row) => <Amount money={row.amount} /> },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="bil-bank-deposits">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
        <Typography variant="subtitle1">{t('bil:bank_deposit.title')}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField select size="small" label={t('bil:session.register')} value={selected?.id ?? ''} onChange={(event) => setRegisterId(event.target.value)} sx={{ minWidth: 180 }}>
            {active.map((register) => (
              <MenuItem key={register.id} value={register.id}>
                {register.name}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" disabled={!selected} onClick={() => setOpen(true)} data-testid="bil-bank-deposit-new">
            {t('bil:bank_deposit.new')}
          </Button>
        </Stack>
      </Stack>
      {deposits.data ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('bil:bank_deposit.available')} <Amount money={deposits.data.availableCash} testId="bil-bank-deposit-cash" />
        </Typography>
      ) : null}
      <DataTable
        captionKey="bil:bank_deposit.title"
        columns={columns}
        rows={deposits.data?.items}
        getRowId={(row) => row.id}
        isLoading={registers.isPending || (selected !== undefined && deposits.isPending)}
        error={registers.error ?? deposits.error}
        emptyTitleKey="bil:bank_deposit.empty"
      />
      {open && selected ? <BankDepositDialog open registerId={selected.id} branchId={selected.branchId} onClose={() => setOpen(false)} /> : null}
    </Paper>
  );
}

/** صف کارتخوان نامعلوم (AC-BIL-032.2): تأیید با کد پیگیری یا رد، همراه دلیل. */
export function PendingPaymentsPanel() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [resolving, setResolving] = useState<BilPendingPaymentView | null>(null);
  const [form, setForm] = useState({ approved: 'true', reference: '', note: '' });
  const pending = useQuery({ queryKey: bilKeys.pending(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/payments/pending')) });
  const resolve = useMutation({
    mutationFn: (payment: BilPendingPaymentView) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/payments/{id}:resolve', {
          params: { path: { id: payment.id } },
          body: { approved: form.approved === 'true', reference: form.reference.trim() || null, cardLast4: null, terminalId: null, note: form.note.trim() },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setResolving(null);
      setForm({ approved: 'true', reference: '', note: '' });
      void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
    },
  });
  const columns: DataTableColumn<BilPendingPaymentView>[] = [
    { id: 'at', headerKey: 'bil:session.opened_at', cell: (row) => <JalaliDate value={row.receivedAt} withTime /> },
    { id: 'patient', headerKey: 'bil:deposits.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'method', headerKey: 'bil:refunds.columns.method', cell: (row) => t(`bil:method.${row.method}`) },
    { id: 'amount', headerKey: 'bil:cashier.amount', cell: (row) => <Amount money={row.amount} /> },
    {
      id: 'actions',
      headerKey: 'bil:session.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setResolving(row)} data-testid="bil-pending-resolve">
          {t('bil:pos.resolve')}
        </Button>
      ),
    },
  ];
  const valid = form.note.trim() !== '' && (form.approved === 'false' || form.reference.trim() !== '');
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="bil-pending-payments">
      <Typography variant="subtitle1" gutterBottom>
        {t('bil:pos.pending_title')}
      </Typography>
      <DataTable
        captionKey="bil:pos.pending_title"
        columns={columns}
        rows={pending.data?.items}
        getRowId={(row) => row.id}
        isLoading={pending.isPending}
        error={pending.error}
        onRetry={() => void pending.refetch()}
        emptyTitleKey="bil:pos.pending_empty"
      />
      <FormDialog
        open={resolving !== null}
        titleKey="bil:pos.resolve"
        onSubmit={() => resolving && valid && resolve.mutate(resolving)}
        onClose={() => setResolving(null)}
        isDirty={form.note !== ''}
        isSubmitting={resolve.isPending}
        submitError={resolve.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <RadioGroup row value={form.approved} onChange={(event) => setForm({ ...form, approved: event.target.value })}>
            <FormControlLabel value="true" control={<Radio />} label={t('bil:pos.outcome.Approved')} />
            <FormControlLabel value="false" control={<Radio />} label={t('bil:pos.outcome.Declined')} />
          </RadioGroup>
          {form.approved === 'true' ? (
            <TextField required label={t('bil:cashier.pos_reference')} value={form.reference} onChange={(event) => setForm({ ...form, reference: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr' } }} />
          ) : null}
          <TextField required label={t('bil:write_off.reason')} value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
        </Stack>
      </FormDialog>
    </Paper>
  );
}
